import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FieldArray, Formik } from "formik";
import {
  AppCard,
  AppInputNumber,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
} from "@/layout/AppComponents";
import { AppCheckbox } from "@/layout/AppComponents";
import ReactDOMServer from "react-dom/server";
import Translator from "../../../../components/helpers/Translator";
import colors from "../../../../../constants/colors";
import { axiosPath } from "../../../../../routing/helpers/router";
import NotificationAlert from "../../../../../layout/components/helpers/notificationAlert";
import errorHandler from "@/layout/components/error/ErrorHandler";
import useIsMounted from "../../../../../layout/components/hooks/useIsMounted";

export default function EmailNotificationSettings(props) {
  const { type, element, onUpdate } = props;
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const { moduleBridgeHex } = useParams();
  const [notifications, setNotifications] = useState();

  // > LOAD EXISTING NOTIFICATIONS
  const loadExistingNotifications = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_correctiveActions_notifications_get"),
          {
            moduleBridgeHex,
            type,
            element,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setNotifications(data);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, type, element]
  );

  useEffect(() => {
    loadExistingNotifications();
  }, []);
  // < LOAD EXISTING NOTIFICATIONS

  const handleSave = useCallback(
    async ({ customEmails, noNotification }) =>
      axios
        .post(
          axiosPath("api_r_user_module_correctiveActions_notifications_set"),
          {
            moduleBridgeHex,
            type,
            element,
            value: {
              customEmails,
              noNotification,
            },
          }
        )
        .then(() => {
          onUpdate();
          NotificationAlert({
            title: ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="updated" />
            ),
            message: ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="changes.successfully.updated"
              />
            ),
            type: "success",
            duration: 5000,
          });
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, type, element]
  );

  const handleAddPeriod = (values, arrayHelpers, setFieldValue) => {
    const period = values.days * values.period;

    if (!values?.customEmails.includes(period)) {
      arrayHelpers.push(values.days * values.period);
      setFieldValue("days", "");
    }
  };

  if (notifications === undefined) {
    return <AppNoInfo />;
  }

  return (
    <div>
      <div className="alert alert-light">
        <Translator
          file="module-legalCompliance"
          name="email.notification.settings.text.1"
        />
        <br />
        <br />
        <Translator
          file="module-legalCompliance"
          name="email.notification.settings.text.2"
        />
        <br />
        <Translator
          file="module-legalCompliance"
          name="email.notification.settings.text.3"
        />
        <br />
        <Translator
          file="module-legalCompliance"
          name="email.notification.settings.text.4"
        />
      </div>
      <Formik
        initialValues={{
          noNotification:
            notifications?.customNotifications?.noNotification || false,
          customEmails: notifications?.customNotifications?.customEmails || [],
          days: "",
          period: -1,
        }}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleSave}
      >
        {({ setFieldValue, handleSubmit, values, errors }) => (
          <form onSubmit={handleSubmit}>
            <FieldArray
              name="customEmails"
              render={(arrayHelpers) => (
                <div className="row">
                  <div className="col-lg-4">
                    <AppCheckbox
                      value={values.noNotification}
                      onChange={(v) => setFieldValue("noNotification", v)}
                      containerClassName="mt-0 mb-3 p-0"
                    >
                      <Translator
                        file="module-legalCompliance"
                        name="email.notification.settings.decline.notifications"
                      />
                    </AppCheckbox>
                    <AppInputNumber
                      title={ReactDOMServer.renderToString(
                        <Translator file="module-legalCompliance" name="days" />
                      )}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="set.num.days"
                        />
                      )}
                      value={values.days}
                      onChange={(v) => setFieldValue("days", v)}
                      decimals={0}
                      required
                      error={errors?.days}
                      icon={<AppRemixIcon icon="calendar" />}
                    />
                    <AppSingleSelect
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="period"
                        />
                      )}
                      value={values.period}
                      options={[
                        {
                          label: ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="period.before"
                            />
                          ),
                          value: -1,
                        },
                        {
                          label: ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="period.after"
                            />
                          ),
                          value: 1,
                        },
                      ]}
                      onChange={(v) => setFieldValue("period", v)}
                      decimals={0}
                      required
                      error={errors.period}
                      icon={<AppRemixIcon icon="calendar" />}
                    />
                    <button
                      type="button"
                      disabled={values.noNotification || values?.days === ""}
                      className="btn btn-block btn-success btn-sm mt-2"
                      onClick={() =>
                        handleAddPeriod(values, arrayHelpers, setFieldValue)
                      }
                    >
                      <Translator file="module-legalCompliance" name="add" />
                    </button>
                  </div>
                  <div className="col-lg-4">
                    <AppCard>
                      {values.noNotification ? (
                        <AppNoInfo
                          text={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="wont.receive.notifications"
                            />
                          )}
                          color={colors.primary}
                          separation={0}
                          icon={
                            <AppRemixIcon
                              size={24}
                              icon="notification-off"
                              color={colors.primary}
                            />
                          }
                        />
                      ) : values?.customEmails &&
                        values?.customEmails.length > 0 ? (
                        <ul style={{ listStyle: "none", padding: 0 }}>
                          {values?.customEmails.map((ce, i) => (
                            <li key={i}>
                              <AppRemixIcon
                                icon="delete-bin"
                                className="mr-2 pointer"
                                color={colors.danger}
                                onClick={() => arrayHelpers.remove(i)}
                              />
                              {ce < 0 ? (
                                <span>
                                  <Translator
                                    file="module-legalCompliance"
                                    name="days.before"
                                    variables={{ days: ce * -1 }}
                                    isHtml
                                  />
                                </span>
                              ) : ce > 0 ? (
                                <span>
                                  <Translator
                                    file="module-legalCompliance"
                                    name="days.after"
                                    variables={{ days: ce }}
                                    isHtml
                                  />
                                </span>
                              ) : (
                                <span>
                                  <Translator
                                    file="module-legalCompliance"
                                    name="deadline.day"
                                  />
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <AppNoInfo
                          text={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="no.custom.period.yet"
                            />
                          )}
                          color={colors.primaryLight}
                          separation={0}
                          icon={
                            <AppRemixIcon
                              size={24}
                              icon="calendar-check"
                              color={colors.primaryLight}
                            />
                          }
                        />
                      )}
                    </AppCard>
                  </div>
                  <div className="col-lg-4">
                    <AppCard>
                      <span className="text-primary mb-2">
                        <Translator
                          file="module-legalCompliance"
                          name="periodicity.by.default"
                        />
                      </span>
                      {notifications?.existingNotifications && (
                        <ul style={{ listStyle: "none", padding: 0 }}>
                          {notifications?.existingNotifications.map((en) => (
                            <li key={en}>
                              {en < 0 ? (
                                <span>
                                  <Translator
                                    file="module-legalCompliance"
                                    name="days.before"
                                    variables={{ days: en * -1 }}
                                    isHtml
                                  />
                                </span>
                              ) : en > 0 ? (
                                <span>
                                  <Translator
                                    file="module-legalCompliance"
                                    name="days.after"
                                    variables={{ days: en }}
                                    isHtml
                                  />
                                </span>
                              ) : (
                                <span>
                                  <Translator
                                    file="module-legalCompliance"
                                    name="deadline.day"
                                  />
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </AppCard>
                  </div>
                  <div className="col-lg-12">
                    <button
                      type="submit"
                      className="btn btn-success float-right"
                    >
                      <Translator
                        file="module-legalCompliance"
                        name="save.changes"
                      />
                    </button>
                  </div>
                </div>
              )}
            />
          </form>
        )}
      </Formik>
    </div>
  );
}
